/**
 * Orders Page Loader — unified /my-orders endpoint returning both
 * standalone orders (type: "order") and package orders (type: "package").
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, getSearchParams, loaderCache } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import {
  fetchMyOrders,
  type MyOrdersParams,
  type MyOrdersRow,
} from '@/services/orderService';

export interface OrdersData {
  rows: MyOrdersRow[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
}

const DEFAULT_NON_CONTENT = 'guest_post,curated_link,visual_link';

function buildParams(searchParams: URLSearchParams): MyOrdersParams {
  const params: MyOrdersParams = {
    sort_by: searchParams.get('sort_by') ?? 'created_at',
    sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') ?? 'desc',
    per_page: Number(searchParams.get('per_page')) || 15,
    page: Number(searchParams.get('page')) || 1,
  };

  // Multi-select types (comma-separated). Default to non-content for orders page.
  params.order_type = searchParams.get('order_type') ?? DEFAULT_NON_CONTENT;
  params.package_type = searchParams.get('package_type') ?? DEFAULT_NON_CONTENT;

  const pass = ['status', 'site_id', 'date_from', 'date_to', 'search'] as const;
  for (const key of pass) {
    const v = searchParams.get(key);
    if (v) (params as Record<string, unknown>)[key] = v;
  }

  if (searchParams.get('has_available_slots') === 'true') {
    params.has_available_slots = true;
  }

  return params;
}

async function fetchOrdersData(
  searchParams: URLSearchParams
): Promise<OrdersData> {
  const cacheKey = `orders-${searchParams.toString()}`;
  const cached = loaderCache.get<OrdersData>(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetchMyOrders(buildParams(searchParams));
    console.log('[/v1/orders/my-orders] response:', response);
    const rows = response.data ?? [];
    const meta = response.meta;
    const data: OrdersData = {
      rows,
      meta: {
        current_page: meta?.current_page ?? 1,
        last_page: meta?.last_page ?? 1,
        per_page: meta?.per_page ?? rows.length,
        total: meta?.total ?? rows.length,
        from: meta?.from ?? null,
        to: meta?.to ?? null,
      },
    };
    loaderCache.set(cacheKey, data, 2 * 60 * 1000);
    return data;
  } catch (error) {
    console.error('[ordersLoader] Failed to fetch orders:', error);
    return {
      rows: [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 0,
        total: 0,
        from: null,
        to: null,
      },
    };
  }
}

export async function ordersLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();
  try {
    await requireAuth(args.request);
    const searchParams = getSearchParams(args);
    const data = await fetchOrdersData(searchParams);

    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/orders', loadTime, dataSize);
    return data;
  } catch (error) {
    if (error instanceof Response) throw error;
    trackRouteError('/orders', error as Error);
    throw error;
  }
}
